class EnquiryDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#EnquiryDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.selectors = {
      form: ".enquiry-drawer__contact-form",
      formSuccess: ".enquiry-drawer__form-success"
    };
    this.identityFields = [
      {
        key: "$first_name",
        selector: "#ProductEnquiry-name"
      },
      {
        key: "$email",
        selector: "#ProductEnquiry-email"
      },
      {
        key: "$phone_number",
        selector: "#ProductEnquiry-phone"
      }
    ];
    this.enquiryFields = [
      {
        key: "product_name",
        selector: "#enquiryProductTitleInput"
      },
      {
        key: "product_id",
        selector: "#enquiryProductIdInput"
      },
      {
        key: "product_image",
        selector: "#enquiryProductImage"
      },
      {
        key: "variant_name",
        selector: "#enquiryVariantTitleInput"
      },
      {
        key: "variant_id",
        selector: "#enquiryVariantIdInput"
      },
      {
        key: "variant_price",
        selector: "#enquiryVariantPriceInput"
      },
      {
        key: "delivery_location",
        selector: "#ProductEnquiry-delivery-location"
      },
      {
        key: "notes",
        selector: "#ProductEnquiry-body"
      },
      {
        key: "custom_quote",
        selector: "#ProductEnquiry-custom-quote"
      },
      {
        key: "fabric_swatches",
        selector: "#ProductEnquiry-fabric-swatches"
      }
    ];

  }

  connectedCallback() {
    const enquiryForm = this.querySelector("form#ProductEnquiry");

    // Spam Keywords + Min Submit Time logic
    const spamKeywords = [
      'free money',
      'work from home',
      'buy now',
      'cheap pills',
      'viagra',
      'porn',
      'sex',
      'loan',
      'bitcoin',
      'crypto',
    ];

    const urlRegex = /\b((https?:\/\/|www\.)[^\s]+|\b[a-z0-9-]+\.(com|net|org|io|co|biz|info|me|uk|au|ca|de|fr|es|it|nl)\b)/i;
    const auPhoneRegex = /^(?:\+61|0)[2-478](?:[ -]?\d){8}$/;

    enquiryForm.addEventListener('submit', (event) => {
      event.preventDefault(); // stop default submission first
      event.stopImmediatePropagation();

      // Honeypot check (bots only)
      const honeypotField = this.querySelector('#enquiryWebsite');
      if (honeypotField && honeypotField.value.trim() !== '') {
        // Bot detected — Silently Abort
        return false;
      }

      // Check Fields
      const nameField = this.querySelector('#ProductEnquiry-name');
      const emailField = this.querySelector('#ProductEnquiry-email');
      const phoneField = this.querySelector('#ProductEnquiry-phone');
      const commentField = this.querySelector('#ProductEnquiry-body');

      const name = nameField?.value.toLowerCase() || '';
      const email = emailField?.value.toLowerCase() || '';
      const comment = commentField?.value.toLowerCase() || '';
      const allText = name + ' ' + email + ' ' + comment;

      // Clear previous warnings
      this.querySelectorAll('.spam-warning').forEach(el => el.remove());

      let hasError = false;

      // Field Validation
      const patternFields = [
        { field: this.querySelector('#ProductEnquiry-name'), message: 'Please enter a valid name.' },
        { field: this.querySelector('#ProductEnquiry-email'), message: 'Please enter a valid email address.' },
        { field: phoneField, message: 'Please enter a valid phone number.' }
      ];

      patternFields.forEach(({ field, message }) => {
        if (!field) return;

        let invalid = false;

        if (field === phoneField) {
          const phoneValue = field.value.trim();

          // Allow empty, Validate only if filled
          invalid = phoneValue !== '' && !auPhoneRegex.test(phoneValue);
        } else {
          invalid = !field.checkValidity();
        }

        if (invalid) {
          hasError = true;

          const warning = document.createElement('small');
          warning.className = 'spam-warning text-red-600';
          warning.innerText = message;

          const contactFields = this.querySelector('.contact__fields');
          if (contactFields) {
            contactFields.appendChild(warning);
          }
        }
      });

      // Spam Keyword Check
      const foundSpam = spamKeywords.some(keyword => allText.includes(keyword));
      if (foundSpam) {

        hasError = true;

        const warning = document.createElement('small');
        warning.className = 'spam-warning text-red-600';
        warning.innerText = 'Some words or phrases in your message may be flagged as spam. Please revise and try again.';
        
        // Append to the bottom of .contact__fields
        const contactFields = this.querySelector('.contact__fields');
        if (contactFields) {
          contactFields.appendChild(warning);
        }
      }

      // Website URL Check (Comment Field)
      if (urlRegex.test(comment)) {
        hasError = true;

        const warning = document.createElement('small');
        warning.className = 'spam-warning text-red-600';
        warning.innerText = 'Please remove website links from your message.';

        const contactFields = this.querySelector('.contact__fields');
        if (contactFields) {
          contactFields.appendChild(warning);
        }
      }

      // Minimum Submit Time Check
      const startTime = parseInt(this.querySelector('#formStartTime')?.value, 10) || 0;
      const now = Math.floor(Date.now() / 1000);
      if (now - startTime < 3) {
        const warning = document.createElement('small');
        warning.className = 'spam-warning text-red-600';
        warning.innerText = 'Please take a moment to fill out the form.';
        commentField.parentNode.appendChild(warning);
        hasError = true;
      }

      if (hasError) return false; // Stop Submission

      this.submitFormToKlaviyo();
      return false;
    });
  }

  getFieldValues(fields) {
    return fields.reduce((prev, curr) => {
      const element = this.querySelector(curr.selector);
      let value = "";
      if (element.type === "text" || element.type === "email" || element.type === "tel" || element.type === "textarea" || element.type === "hidden") {
        value = element.value;
      }
      if (element.type === "checkbox") {
        value = element.checked;
      }
      return {
        ...prev,
        [curr.key]: value
      }
    }, {})
  }

  pushToDataLayer(enquiryFields) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      'event': 'generate_lead',
      'item_name': enquiryFields.product_name,
      'variant_name': enquiryFields.variant_name,
      'type': 'product_enquiry'
    });
  }

  submitFormToKlaviyo() {
    const _learnq = window._learnq || [];

    let identifyFields = this.getFieldValues(this.identityFields);

    if (identifyFields["$first_name"].includes(" ")) {
      const nameParts = identifyFields["$first_name"].split(" ");
      const firstName = nameParts[0];
      const lastName = nameParts.slice(1).join(" ");
      identifyFields = {
        ...identifyFields,
        ["$first_name"]: firstName,
        ["$last_name"]: lastName
      }
    }
    const enquiryFields = this.getFieldValues(this.enquiryFields);

    if (_learnq) {
      // Identify Profile
      _learnq.push(["identify", identifyFields]);
      // Push Enquiry to Klaviyo
      _learnq.push(["track", "Product Enquiry", enquiryFields, () => {
        this.showSuccess();
        this.pushToDataLayer(enquiryFields);
      }]);
    }
  }

  resetFieldValues(fields) {
    fields.forEach((field) => {
      const element = this.querySelector(field.selector);
      if (element.type === "text" || element.type === "email" || element.type === "tel" || element.type === "textarea") {
        element.value = "";
      }
      if (element.type === "checkbox") {
        element.checked = false;
      }
    });
  }

  setHeaderCartIconAccessibility() {
    const variantDrawerTriggers = document.querySelectorAll('.enquiry-drawer__trigger');
    variantDrawerTriggers.forEach((variantDrawerTrigger) => {
      variantDrawerTrigger.setAttribute('role', 'button');
      variantDrawerTrigger.setAttribute('aria-haspopup', 'dialog');
      variantDrawerTrigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(variantDrawerTrigger);
      });
      variantDrawerTrigger.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(variantDrawerTrigger);
        }
      });
    });
  }

  showSuccess() {
    const formElement = this.querySelector(this.selectors.form);
    const formSuccessElement = this.querySelector(this.selectors.formSuccess);
    formElement.classList.add("hidden");
    formSuccessElement.classList.remove("hidden");
  }

  reset() {
    const formElement = this.querySelector(this.selectors.form);
    const formSuccessElement = this.querySelector(this.selectors.formSuccess);
    formElement.classList.remove("hidden");
    formSuccessElement.classList.add("hidden");
    this.resetFieldValues(this.identityFields);
    this.resetFieldValues(this.enquiryFields);
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = document.getElementById('EnquiryDrawer');
        const focusElement = this.querySelector('.enquiry-drawer__inner') || this.querySelector('.enquiry-drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    // document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    // document.body.classList.remove('overflow-hidden');
    this.reset();
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('enquiry-drawer', EnquiryDrawer);

